import { visualizeAudio, type MediaUtilsAudioData } from '@remotion/media-utils'

/**
 * The amplitude scale behind the windowed FFT, kept pure and DOM-free so it can
 * be unit-tested against Remotion's real `visualizeAudio` in Node.
 *
 * ## Why any of this exists
 *
 * `visualizeAudio` divides every magnitude by `toInt16(max positive sample of
 * channelWaveforms[0])` — the loudest sample of *the data it was handed*,
 * cached per `resultId`. With `useAudioData` that was the whole file, so the
 * scale was constant. With `useWindowedAudioData` the data is three 10-second
 * windows and the `resultId` changes with them, so a quiet passage would render
 * 10-20 dB hotter than a loud one and the scale would step at every window
 * boundary.
 *
 * {@link rescaleFrequencyData} undoes that: multiply by the window's own peak
 * and divide by the whole file's, recovering the constant denominator the
 * unwindowed call had.
 *
 * ## The fidelity contract
 *
 * The whole-file peak comes from `waveform.dat`, which `audiowaveform` writes
 * **mono-mixed** and at **8-bit** resolution (no `--split-channels`,
 * `--bits 8`). So it is not bit-identical to `max(channel 0)`:
 *
 * - Quantisation costs `1/(128 * peak)` in relative terms — 0.14 dB for content
 *   peaking at -6 dBFS, around 2 dB for a recording peaking at -30 dBFS.
 * - A mono mix of opposite-phase stereo can read lower than channel 0 alone,
 *   and in the limit reads 0 — which falls back to the window-local scale.
 *
 * Near-parity for anything mastered normally. An exact channel-0 peak would
 * need the server-side decode pipeline this change exists to avoid.
 */

/**
 * Remotion's asymmetric float-to-int16 scaling, copied because
 * `getMaxPossibleMagnitude` is not exported from `@remotion/media-utils`.
 *
 * Asymmetric on purpose — int16 holds one more negative value than positive —
 * and it must match `fft/to-int-16.ts` exactly, because {@link
 * rescaleFrequencyData} divides out a denominator Remotion computed with it.
 */
export const toInt16 = (x: number): number => (x > 0 ? x * 0x7fff : x * 0x8000)

/**
 * The largest positive sample, mirroring Remotion's `getMax` — negatives are
 * ignored rather than absolute-valued, because `toInt16` scales the two signs
 * differently. Empty (or all-negative) input gives `0`.
 */
export function maxPositiveSample(samples: Float32Array): number {
  let max = 0
  for (let i = 0; i < samples.length; i++) {
    const value = samples[i]!
    if (value > max) max = value
  }
  return max
}

/**
 * Puts window-local magnitudes back on the whole-file scale.
 *
 * `globalPeak` null/undefined/non-positive means we have no whole-file number
 * to rescale against — an asset without a `waveform.dat`, or a mono mix that
 * cancelled to silence — so the values pass through unchanged and the bars keep
 * today's window-local behaviour rather than disappearing.
 *
 * The ratio is clamped to 1. It can legitimately exceed 1 — the 8-bit peak
 * rounds down, and a mono mix can read below channel 0's true peak — and
 * amplifying past the unwindowed scale would clip the bars at the top of their
 * range. Clamping keeps the error one-sided and small.
 */
export function rescaleFrequencyData(
  values: number[],
  localMax: number,
  globalPeak: number | null | undefined,
): number[] {
  if (globalPeak == null || globalPeak <= 0) return values
  if (localMax <= 0) return values

  const ratio = Math.min(1, toInt16(localMax) / toInt16(globalPeak))
  if (ratio === 1) return values

  return values.map((value) => value * ratio)
}

/**
 * Whether the loaded windows actually contain every sample the frame's
 * visualization will read.
 *
 * `visualizeAudio` with smoothing computes frames `f-1`, `f` and `f+1`, and
 * each reads `sampleSize` samples starting `sampleSize / 2` before its own
 * position — so the real span is `[(f-1)/fps - 512/sr, (f+1)/fps + 512/sr]` for
 * the 512-sample-per-bin call we make.
 *
 * The guard is needed because `useWindowedAudioData` releases its own
 * `delayRender` as soon as the *current* window resolves and fetches the two
 * neighbours afterwards. At a window boundary the smoothing terms would
 * otherwise be computed against whatever samples happened to be adjacent in the
 * concatenated buffer — a visible amplitude step every 10 seconds.
 *
 * Clamped to `[0, durationInSeconds]` so the first and last frames of a file,
 * where the span runs off the end of the media, are not held forever waiting
 * for samples that do not exist.
 */
export function isWindowCovered({
  frame,
  fps,
  sampleRate,
  dataOffsetInSeconds,
  dataLength,
  durationInSeconds,
  halfSampleSize = 512,
}: {
  frame: number
  fps: number
  sampleRate: number
  dataOffsetInSeconds: number
  dataLength: number
  durationInSeconds: number
  halfSampleSize?: number
}): boolean {
  if (dataLength <= 0 || sampleRate <= 0) return false

  const pad = halfSampleSize / sampleRate
  const neededStart = Math.max(0, (frame - 1) / fps - pad)
  const neededEnd = Math.min(durationInSeconds, (frame + 1) / fps + pad)

  const availableStart = dataOffsetInSeconds
  const availableEnd = dataOffsetInSeconds + dataLength / sampleRate

  return neededStart >= availableStart && neededEnd <= availableEnd
}

/**
 * How many windows `useWindowedAudioData` will load for this frame — an exact
 * mirror of its own `windowsToFetch`: the current window, the one before it
 * unless it is the first, and the one after unless it is past the end.
 *
 * Paired with {@link loadedWindowCount}, this is what tells the coverage guard
 * whether a fetch is still outstanding. Without that distinction the guard
 * cannot tell "wait, the neighbour is coming" from "this is everything there
 * will ever be" — and holding a `delayRender` for the second case is a render
 * timeout, the same symptom as the OOM the windowed decode exists to fix.
 */
export function expectedWindowCount({
  frame,
  fps,
  windowInSeconds,
  durationInSeconds,
}: {
  frame: number
  fps: number
  windowInSeconds: number
  durationInSeconds: number
}): number {
  const currentWindowIndex = Math.floor(frame / fps / windowInSeconds)
  // The same epsilon Remotion uses, so a duration that is an exact multiple of
  // the window size does not name an empty window past the end.
  const maxWindowIndex = Math.floor(
    durationInSeconds / windowInSeconds - 0.000000000001,
  )

  let count = 1
  if (currentWindowIndex > 0) count++
  if (currentWindowIndex + 1 <= maxWindowIndex) count++
  return count
}

/**
 * How many windows the concatenated buffer actually spans.
 *
 * Rounded up, because the final window of a file is short — counting whole
 * windows would report a file-end pair as one and make the guard wait forever
 * for a window that does not exist.
 */
export function loadedWindowCount({
  dataLength,
  sampleRate,
  windowInSeconds,
}: {
  dataLength: number
  sampleRate: number
  windowInSeconds: number
}): number {
  if (dataLength <= 0 || sampleRate <= 0) return 0
  // The epsilon keeps an exact multiple from rounding up to a phantom window.
  return Math.ceil(dataLength / sampleRate / windowInSeconds - 1e-9)
}

/**
 * The FFT call and the rescale as one unit — the whole transform from decoded
 * samples to the numbers the bars are drawn from.
 *
 * This exists so the parity test exercises the code the hook runs rather than a
 * second copy of it. When the test assembled `visualizeAudio` + `slice` +
 * `rescaleFrequencyData` itself, dropping `dataOffsetInSeconds` from the hook's
 * call, slicing the wrong half, or passing the unshifted frame all left every
 * assertion green while every render drew wrong bars. `useFrequencyData` keeps
 * the parts that need React — the window hook, the peak, the coverage guard —
 * and calls this for the arithmetic.
 *
 * `numberOfSamples` is 512, so `getVisualization` reads 1024 samples per frame
 * and the result is 512 bins; only the lower half is kept, because the upper
 * half of an FFT of real input mirrors it.
 */
export const NUMBER_OF_SAMPLES = 512

export function frequencyValues({
  audioData,
  frame,
  fps,
  dataOffsetInSeconds,
  localMax,
  globalPeak,
}: {
  audioData: MediaUtilsAudioData
  frame: number
  fps: number
  dataOffsetInSeconds: number
  localMax: number
  globalPeak: number | null | undefined
}): number[] {
  const values = visualizeAudio({
    audioData,
    frame,
    fps,
    numberOfSamples: NUMBER_OF_SAMPLES,
    smoothing: true,
    dataOffsetInSeconds,
  })

  return rescaleFrequencyData(
    values.slice(0, NUMBER_OF_SAMPLES / 2),
    localMax,
    globalPeak,
  )
}
